import React, { Fragment, useEffect, useState } from 'react'
import { ProductsPageProps, ProductTableExpandableEntities } from './ProductsPage.types'
import styles from './ProductsPage.module.scss'
import cl from 'classnames'
import {
  PageHeader, PaginationButtons, TableBodyContainer,
  TableBodyLine, TableCellWithAdditionalCount, TableContainer, TableExpandableCell,
  TableHeader, TablePeriodFilter, TableSliderFilter, TableTagsCell, Text1
} from 'components/shared'
import { inject } from 'mobx-react'
import { GENERAL_STORE, ProductsTableSortProps, PRODUCT_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import { getMainImageSrcForObj, SortDirection } from 'utilities'
import { IconProducts } from 'assets'
import { WarningMessage } from 'components'
import { ImageSquare } from '@touche-lts/panel-ui/dist'
import tableBodyContainerStyle from '../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'

const ProductsPageComponent = (props: ProductsPageProps) => {
  const {
    [PRODUCT_STORE]: {
      getSearchedProducts,
      productsList,
      paginationInfo,
      onPageSelect,
      getSortDirection,
      setSortDirection,
      isColumnMarked,
      searchProps,
      onSearchPropsChange,
      skuCountRange,
      onSkuCountChange,
      createdDatePeriodFilter,
      onCreatedDateFilterChange,
      onProductsTableDidMount
    },
    [GENERAL_STORE]: {
      push
    }
  } = props

  useEffect(() => {
    return onProductsTableDidMount
    // eslint-disable-next-line
  }, [])
  useEffect(getSearchedProducts, [getSearchedProducts])

  // const tableColWidths = {
  //   products: 20,
  //   activeSkus: 10,
  //   stores: 15,
  //   projects: 16,
  //   activeAgreements: 16,
  //   addedToStudio: 11,
  //   tags: 12
  // }
  const tableColWidths = {
    products: 340,
    activeSkus: 200,
    stores: 260,
    projects: 280,
    activeAgreements: 280,
    addedToStudio: 180,
    tags: 265
  }

  const handleSetSortDirection = (sortProp: ProductsTableSortProps) =>
    (direction: SortDirection) => {
      setSortDirection(sortProp, direction)
    }

  const [expandedProducts, setExpandedProducts] =
    useState<{ [key: number]: ProductTableExpandableEntities }>({})

  const onExpandClick = (productId: number, entity: ProductTableExpandableEntities) => () => {
    const isExpanded = expandedProducts[productId] === entity
    const newValue = isExpanded ? null : entity
    setExpandedProducts(s => ({
      ...s,
      [productId]: newValue
    }))
  }

  const scrollbarSpacing = 4.8
  return (
    <div className={cl(styles.container)}>
      <PageHeader
        navSegments={[{
          label: 'Products',
          icon: IconProducts
        }]}
      />
      <TableContainer className={styles.tableFill} style={{ height: 'auto' }}>
        <div className={cl(styles.tableScroll, styles.thinScroll)}>

        <TableHeader
          tableColumns={[
            {
              label: 'products',
              // widthPercentage: tableColWidths.products,
              sortDirection: getSortDirection(ProductsTableSortProps.NAME),
              setSortDirection: handleSetSortDirection(ProductsTableSortProps.NAME),
              isColumnMarked: isColumnMarked(ProductsTableSortProps.NAME),
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.productName,
                onChange: productName => onSearchPropsChange({ productName })
              },
              widthPx: tableColWidths.products
            },
            {
              label: 'active skus',
              // widthPercentage: tableColWidths.activeSkus,
              // sortDirection: getSortDirection(ProductsTableSortProps.),
              // setSortDirection: handleSetSortDirection(ProductsTableSortProps.),
              filterComponent: (
                <TableSliderFilter
                  label='SKUs'
                  min={skuCountRange.min}
                  max={skuCountRange.max}
                  onChange={onSkuCountChange}
                />
              ),
              widthPx: tableColWidths.activeSkus
            },
            {
              label: 'stores',
              // widthPercentage: tableColWidths.stores,
              sortDirection: getSortDirection(ProductsTableSortProps.STORE_NAME),
              setSortDirection: handleSetSortDirection(ProductsTableSortProps.STORE_NAME),
              isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME),
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.brandName,
                onChange: brandName => onSearchPropsChange({ brandName })
              },
              widthPx: tableColWidths.stores
            },
            {
              label: 'projects',
              // widthPercentage: tableColWidths.projects,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.projectName,
                onChange: projectName => onSearchPropsChange({ projectName })
              },
              widthPx: tableColWidths.projects
            },
            {
              label: 'active agreements',
              // widthPercentage: tableColWidths.activeAgreements,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.agreementName,
                onChange: agreementName => onSearchPropsChange({ agreementName })
              },
              widthPx: tableColWidths.activeAgreements
            },
            {
              label: 'added to studio',
              // widthPercentage: tableColWidths.addedToStudio,
              sortDirection: getSortDirection(ProductsTableSortProps.CREATED_DATE),
              setSortDirection: handleSetSortDirection(ProductsTableSortProps.CREATED_DATE),
              isColumnMarked: isColumnMarked(ProductsTableSortProps.CREATED_DATE),
              filterComponent: (
                <TablePeriodFilter
                  period={createdDatePeriodFilter}
                  setPeriod={onCreatedDateFilterChange}
                />
              ),
              widthPx: tableColWidths.addedToStudio
            },
            {
              label: 'tags',
              // widthPercentage: tableColWidths.tags,
              sortDirection: getSortDirection(ProductsTableSortProps.TAGS),
              setSortDirection: handleSetSortDirection(ProductsTableSortProps.TAGS),
              isColumnMarked: isColumnMarked(ProductsTableSortProps.TAGS),
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.tags,
                onChange: tags => onSearchPropsChange({ tags })
              },
              widthPx: tableColWidths.tags + scrollbarSpacing
            }
          ]}
        />
        <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer} >
          {productsList.map(product => {
            const isExpanded = !!expandedProducts[product.id]

            const renderExpandedProjectsLines = () => {
              const entries = Array.from(product.projects.entries())

              return (
                entries.map(([projectName, agreements], index) => {
                  const isLast = index === entries.length - 1

                  return (
                    <TableBodyLine
                      key={index}
                      cells={[
                        {
                          // widthPercentage: tableColWidths.products
                          widthPx: tableColWidths.products
                        },
                        {
                          // widthPercentage: tableColWidths.activeSkus
                          widthPx: tableColWidths.activeSkus
                        },
                        {
                          // widthPercentage: tableColWidths.stores
                          widthPx: tableColWidths.stores
                        },
                        {
                          // widthPercentage: tableColWidths.projects,
                          className: cl(styles.expandedLeft, {
                            [styles.expandedBottom]: isLast
                          }),
                          children: <Text1 overflowEllipsis>{projectName}</Text1>,
                          widthPx: tableColWidths.projects
                        },
                        {
                          // widthPercentage: tableColWidths.activeAgreements,
                          className: cl(styles.expandedMiddle, {
                            [styles.expandedBottom]: isLast
                          }),
                          children: (
                            <TableCellWithAdditionalCount
                              text={Array.from(agreements.values())[0]}
                              totalCount={agreements.size}
                            />
                          ),
                          widthPx: tableColWidths.activeAgreements
                        },
                        {
                          // widthPercentage: tableColWidths.addedToStudio,
                          className: cl(styles.expandedMiddle, {
                            [styles.expandedBottom]: isLast
                          }),
                          widthPx: tableColWidths.addedToStudio
                        },
                        {
                          // widthPercentage: tableColWidths.tags,
                          className: cl(styles.expandedRight, {
                            [styles.expandedBottom]: isLast
                          }),
                          widthPx: tableColWidths.tags
                        }
                      ]}
                    />
                  )
                })
              )
            }

            const renderExpandedAgreementsLines = () => {
              const entries = Array.from(product.agreements.entries())

              return (
                entries.map(([agreementName, projects], index) => {
                  const isLast = index === entries.length - 1

                  return (
                    <TableBodyLine
                      key={index}
                      cells={[
                        {
                          // widthPercentage: tableColWidths.products
                          widthPx: tableColWidths.products
                        },
                        {
                          // widthPercentage: tableColWidths.activeSkus
                          widthPx: tableColWidths.activeSkus
                        },
                        {
                          // widthPercentage: tableColWidths.stores
                          widthPx: tableColWidths.stores
                        },
                        {
                          // widthPercentage: tableColWidths.projects,
                          className: cl(styles.expandedLeft, {
                            [styles.expandedBottom]: isLast
                          }),
                          children: (
                            <TableCellWithAdditionalCount
                              text={Array.from(projects.values())[0]}
                              totalCount={projects.size}
                            />
                          ),
                          widthPx: tableColWidths.projects
                        },
                        {
                          // widthPercentage: tableColWidths.activeAgreements,
                          className: cl(styles.expandedMiddle, {
                            [styles.expandedBottom]: isLast
                          }),
                          children: <Text1 overflowEllipsis>{agreementName}</Text1>,
                          widthPx: tableColWidths.activeAgreements
                        },
                        {
                          // widthPercentage: tableColWidths.addedToStudio,
                          className: cl(styles.expandedMiddle, {
                            [styles.expandedBottom]: isLast
                          }),
                          widthPx: tableColWidths.addedToStudio
                        },
                        {
                          // widthPercentage: tableColWidths.tags,
                          className: cl(styles.expandedRight, {
                            [styles.expandedBottom]: isLast
                          }),
                          widthPx: tableColWidths.tags
                        }
                      ]}
                    />
                  )
                })
              )
            }

            const renderExpandedLines = () => {
              if (!isExpanded) return null

              return expandedProducts[product.id] === ProductTableExpandableEntities.PROJECT
                ? renderExpandedProjectsLines()
                : renderExpandedAgreementsLines()
            }

            return (
              <Fragment key={product.id}>
                <TableBodyLine
                  cells={[
                    {
                      // widthPercentage: tableColWidths.products,
                      children: (
                        <>
                          <ImageSquare src={getMainImageSrcForObj(product)} />
                          <Text1 bold overflowEllipsis style={{ marginLeft: 12 }}>
                            {product.name}
                          </Text1>
                          <WarningMessage
                            object={product}
                            objectType='product'
                          />
                        </>
                      ),
                      onClick: () => push(`/products/edit/${product.id}`),
                      isColumnMarked: isColumnMarked(ProductsTableSortProps.NAME),
                      widthPx: tableColWidths.products
                    },
                    {
                      // widthPercentage: tableColWidths.activeSkus,
                      children: <Text1>{product.skuCount}</Text1>,
                      widthPx: tableColWidths.activeSkus
                    },
                    {
                      // widthPercentage: tableColWidths.stores,
                      children: <Text1 overflowEllipsis>{product.brandName}</Text1>,
                      isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME),
                      widthPx: tableColWidths.stores
                    },
                    {
                      // widthPercentage: tableColWidths.projects,
                      className: cl({ [styles.expandedTopLeft]: isExpanded }),
                      children: (
                        <TableExpandableCell
                          entityName={ProductTableExpandableEntities.PROJECT}
                          objects={Array.from(product.projects.keys())}
                          onClick={onExpandClick(
                            product.id, ProductTableExpandableEntities.PROJECT
                          )}
                          isExpanded={
                            expandedProducts[product.id] ===
                            ProductTableExpandableEntities.PROJECT
                          }
                        />
                      ),
                      widthPx: tableColWidths.projects
                    },
                    {
                      // widthPercentage: tableColWidths.activeAgreements,
                      className: cl({ [styles.expandedTop]: isExpanded }),
                      children: (
                        <TableExpandableCell
                          entityName={ProductTableExpandableEntities.AGREEMENT}
                          objects={Array.from(product.agreements.keys())}
                          onClick={onExpandClick(
                            product.id, ProductTableExpandableEntities.AGREEMENT
                          )}
                          isExpanded={
                            expandedProducts[product.id] ===
                            ProductTableExpandableEntities.AGREEMENT
                          }
                        />
                      ),
                      widthPx: tableColWidths.activeAgreements
                    },
                    {
                      // widthPercentage: tableColWidths.addedToStudio,
                      className: cl({ [styles.expandedTop]: isExpanded }),
                      children: <Text1>{product.createdDate.format('DD.MM.YYYY')}</Text1>,
                      isColumnMarked: isColumnMarked(ProductsTableSortProps.CREATED_DATE),
                      widthPx: tableColWidths.addedToStudio
                    },
                    {
                      // widthPercentage: tableColWidths.tags,
                      className: cl({ [styles.expandedTopRight]: isExpanded }),
                      children: <TableTagsCell tags={product.tags} />,
                      isColumnMarked: isColumnMarked(ProductsTableSortProps.TAGS),
                      widthPx: tableColWidths.tags
                    }
                  ]}
                />
                {renderExpandedLines()}
              </Fragment>
            )
          })}
        </TableBodyContainer>
        </div>

        <PaginationButtons
          {...paginationInfo}
          onPageSelect={onPageSelect}
        />

      </TableContainer>

    </div>
  )
}

const ProductsPage = inject(PRODUCT_STORE, GENERAL_STORE)(observer(ProductsPageComponent))
export { ProductsPage }
